import { DecisionGraph, type GraphJson, isStoredDecisionGraph, legacyGraphToDecisionGraph } from "./DecisionGraph";
import { processGraphForFrontend } from "../DocumentUtils";
import type { DecisionGraphRelease } from "./DecisionGraphRelease";

/**
 * What views need from a DecisionGraph beyond its own id API: a DecisionGraph for graphs that aren't one yet (a
 * release's rule graph, a graphlib graph built for an older saved result), per-template lookups that stand in for
 * scanning every node, and the small graphlib JSON a renderer needs for the nodes it actually draws.
 */

type ReleaseLike = DecisionGraphRelease | { rule_graph?: any; documents?: any };

const converted = new WeakMap<object, WeakMap<object, DecisionGraph> | DecisionGraph>();
const NO_RELEASE = {};

/**
 * A graph read with a release's rule data, for views: a legacy JSON (or graphlib) graph, or a decision graph read
 * again with `release` (made once per graph object and release). Node objects take their rule data from `release`;
 * without one, from the graph's own (global) nodes, and a decision graph is returned as it is.
 */
export const graphToDecisionGraph = (
  graph: GraphJson | DecisionGraph | Record<string, any>,
  options: { release?: ReleaseLike | null; session?: boolean } = {},
): DecisionGraph => {
  if (graph instanceof DecisionGraph && !options.release) return graph;
  const releaseKey = (options.release ?? NO_RELEASE) as object;
  let byRelease = converted.get(graph) as WeakMap<object, DecisionGraph> | undefined;
  const cached = byRelease?.get(releaseKey);
  if (cached) return cached;
  const bytes = graph instanceof DecisionGraph ? graph.toBytes()
    : legacyGraphToDecisionGraph(DecisionGraph.from(graph).toJSON() as any, { session: options.session ?? false, ruleData: !options.release });
  const decisionGraph = DecisionGraph.from(bytes, options.release ? { release: options.release } : {});
  if (!byRelease) {
    byRelease = new WeakMap();
    converted.set(graph, byRelease);
  }
  byRelease.set(releaseKey, decisionGraph);
  return decisionGraph;
};

const releaseGraphs = new WeakMap<object, { graph: DecisionGraph; ruleGraph: DecisionGraph }>();

/**
 * A release's compiled decision graph, with its rule graph read back as the releases service sends the JSON (document
 * references as names: processGraphForFrontend over the release record, as the service does). Node objects take
 * their rule data from that, so they're what they were from the JSON. Made once per fetched decision graph.
 */
const compiledReleaseGraph = (release: { decisionGraph?: unknown; documents?: any } & Record<string, any>) => {
  const stored = release.decisionGraph as object;
  let compiled = releaseGraphs.get(stored);
  if (!compiled) {
    const ruleGraph = DecisionGraph.from(stored as any);
    const { rule_graph: _ruleGraph, decisionGraph: _decisionGraph, ...record } = release;
    processGraphForFrontend(record, ruleGraph);
    const graph = DecisionGraph.from(stored as any, { release: { ruleNode: ruleNodeOf(ruleGraph), documents: release.documents } });
    compiled = { graph, ruleGraph };
    releaseGraphs.set(stored, compiled);
  }
  return compiled;
};

/**
 * The release's rule graph as a DecisionGraph (every node global, rule data from the release), made once per release:
 * its compiled decision graph when the release came with one (`decisionGraph`, GET releases/<id>?graph=dg),
 * otherwise made from its rule graph JSON.
 */
export const releaseDecisionGraph = (
  release: { rule_graph?: any; documents?: any; decisionGraph?: unknown } | null | undefined,
) => {
  if (release && isStoredDecisionGraph(release.decisionGraph)) return compiledReleaseGraph(release).graph;
  const ruleGraph = release?.rule_graph;
  if (!ruleGraph || (!(ruleGraph instanceof DecisionGraph) && !Array.isArray(ruleGraph.nodes))) return undefined;
  return graphToDecisionGraph(ruleGraph, { release });
};

/**
 * A release fetched with its compiled decision graph (`decisionGraph`) also given its rule graph JSON, read from that
 * decision graph exactly as the releases service sends the JSON (document references as names, see
 * processGraphForFrontend), for code that works on the JSON. Returns the release; one without a decision graph is
 * returned as it is.
 */
export const releaseWithRuleGraph = <R extends { rule_graph?: any; documents?: any; decisionGraph?: unknown } & Record<string, any>>(
  release: R,
): R => {
  if (!release || !isStoredDecisionGraph(release.decisionGraph)) return release;
  const { ruleGraph } = compiledReleaseGraph(release);
  return ruleGraph ? { ...release, rule_graph: ruleGraph.toJSON() } : release;
};

/** A rule graph's node data by guid, as a release's `ruleNode`. */
const ruleNodeOf = (rules: DecisionGraph) => (guid: string) => {
  const id = rules.ref(guid);
  return id === undefined ? undefined : rules.node(id);
};

/**
 * A session's (or solve's) graph read with its release's rule graph: each node is the rule node with the session's
 * values over it, its path, index and parent path, decoded only when read. The session graph can be a decision graph
 * (or its bytes or saved form) or a legacy JSON one (read as a session's decision graph). Without rule nodes, the
 * session graph as it is.
 */
export const sessionGraphWithRelease = (session: unknown, ruleGraph: unknown): DecisionGraph => {
  const bytes = session instanceof DecisionGraph ? session.toBytes()
    : session instanceof Uint8Array || isStoredDecisionGraph(session) ? DecisionGraph.from(session as any).toBytes()
    // a legacy JSON session graph keeps its records and its edges' order (rule order), as the legacy graph had them
    : legacyGraphToDecisionGraph(DecisionGraph.from(session as any).toJSON() as any, { session: true, ruleData: true });
  const rules = ruleGraph ? DecisionGraph.from(ruleGraph as any) : undefined;
  if (!rules || rules.nodeCount() === 0) return DecisionGraph.from(bytes);
  return DecisionGraph.from(bytes, { release: { ruleNode: ruleNodeOf(rules) } });
};

/** Internal release view: its rule graph stays backed by DG rather than JSON. */
export const releaseWithWorkingGraph = <R extends { decisionGraph?: unknown; documents?: any } & Record<string, any>>(release: R): R => {
  if (!release || !isStoredDecisionGraph(release.decisionGraph)) return release;
  return { ...release, rule_graph: compiledReleaseGraph(release).ruleGraph };
};
